'use client';

import {
  Box,
  HStack,
  Icon,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Portal,
  Progress,
  Stack,
  Text,
} from '@chakra-ui/react';
import { MdInfoOutline } from 'react-icons/md';
import type { NestedSpeculator, PublicNestedSpeculator } from '@habity/types';

interface ScoreBreakdownProps {
  speculator: NestedSpeculator | PublicNestedSpeculator;
}

const SCORE_COMPONENTS = [
  { key: 'score_area', label: 'Område', weight: 30, color: 'blue' },
  { key: 'score_recency', label: 'Aktualitet', weight: 20, color: 'green' },
  { key: 'score_activity', label: 'Aktivitet', weight: 20, color: 'purple' },
  { key: 'score_overlap', label: 'Filterträff', weight: 20, color: 'orange' },
  { key: 'score_specificity', label: 'Profildjup', weight: 10, color: 'pink' },
] as const;

export const ScoreBreakdown = ({ speculator }: ScoreBreakdownProps) => {
  const hasBreakdown = speculator.score_recency != null;
  if (!hasBreakdown) return null;

  return (
    <Popover trigger="hover" placement="bottom-start">
      <PopoverTrigger>
        <Box
          as="button"
          onClick={(e: React.MouseEvent) => {
            e.preventDefault();
            e.stopPropagation();
          }}
          display="inline-flex"
          alignItems="center"
          cursor="pointer"
          _hover={{ opacity: 0.7 }}
        >
          <Icon as={MdInfoOutline} boxSize="18px" color="blackAlpha.800" />
        </Box>
      </PopoverTrigger>
      <Portal>
        <PopoverContent
          w="220px"
          shadow="lg"
          rounded="lg"
          border="1px solid"
          borderColor="gray.100"
          zIndex="popover"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
        >
          <PopoverArrow />
          <PopoverBody p={3}>
            <Text
              fontSize="xs"
              fontWeight="semibold"
              mb={2}
              color="blackAlpha.700"
            >
              Matchningsdetaljer
            </Text>
            <Stack spacing={2}>
              {SCORE_COMPONENTS.map(({ key, label, weight, color }) => {
                const raw = (speculator as Record<string, unknown>)[key];
                const value = typeof raw === 'number' ? raw : Number(raw) || 0;
                const weighted = value * weight;
                const pct = Math.round(value * 100);

                return (
                  <Box key={key}>
                    <HStack justify="space-between" mb="2px">
                      <Text fontSize="2xs" color="blackAlpha.700">
                        {label}
                      </Text>
                      <Text
                        fontSize="2xs"
                        fontWeight="medium"
                        color="blackAlpha.800"
                      >
                        {weighted.toFixed(1)}/{weight}
                      </Text>
                    </HStack>
                    <Progress
                      value={pct}
                      size="xs"
                      colorScheme={color}
                      rounded="full"
                      bg="blackAlpha.100"
                    />
                  </Box>
                );
              })}
            </Stack>
            <HStack
              justify="space-between"
              mt={2}
              pt={2}
              borderTop="1px solid"
              borderColor="gray.100"
            >
              <Text fontSize="xs" fontWeight="semibold" color="blackAlpha.800">
                Totalt
              </Text>
              <Text fontSize="xs" fontWeight="semibold" color="blackAlpha.800">
                {speculator.match_score?.toFixed(0)}/100
              </Text>
            </HStack>
          </PopoverBody>
        </PopoverContent>
      </Portal>
    </Popover>
  );
};
